import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import YouTube from "react-youtube";
import axios from "axios";
import { useNavigate } from "react-router";
import "./WhoWeAre.css";

const WhoWeAre = () => {
  const [videoState, setVideoState] = useState({ playing: false });
  const [about, setAbout] = useState({ introduction: "" });
  const [videoSrc, setVideoSrc] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/about`
        );
        const data = res.data.data;
        setAbout(data || { introduction: "" });

        // Handle video source based on database value
        if (data?.videoFile) {
          if (
            data.videoFile.includes("youtube.com") ||
            data.videoFile.includes("youtu.be")
          ) {
            // YouTube link
            const videoId = extractYouTubeId(data.videoFile);
            setVideoSrc({ type: "youtube", id: videoId, url: data.videoFile });
          } else if (data.videoFile.startsWith("/about/")) {
            // Local file
            setVideoSrc({
              type: "file",
              url: `${process.env.REACT_APP_API_URL}${data.videoFile}`,
            });
          }
        }
      } catch (err) {
        console.error("Error fetching About data", err);
      }
    };

    fetchData();
  }, []);

  const extractYouTubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const opts = {
    height: "100%",
    width: "100%",
    playerVars: {
      autoplay: 1,
      modestbranding: 1,
      rel: 0,
      playsinline: 1,
    },
  };

  return (
    <section className="py-24 bg-gradient-to-br from-gray-900 to-blue-900">
      <div className="container mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="space-y-8"
        >
          <h2 className="text-4xl font-bold text-white mb-6">
            About Bishoftu Finance Office
          </h2>

          <div
            className="text-lg text-gray-300 leading-relaxed text-justify line-clamp-10"
            dangerouslySetInnerHTML={{
              __html: about.introduction || "No introduction available",
            }}
          />

          <div className="flex gap-4">
            <button
              onClick={() => navigate("/about")}
              className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all transform hover:scale-105"
            >
              Learn More
            </button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="relative aspect-video bg-gray-800 rounded-2xl overflow-hidden shadow-xl"
        >
          {videoSrc ? (
            <>
              {!videoState.playing && (
                <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-tr from-blue-600/30 to-purple-600/30">
                  <button
                    onClick={() => setVideoState({ playing: true })}
                    className="p-6 bg-white/10 backdrop-blur-sm rounded-full hover:bg-white/20 transition-all animate-pulse"
                  >
                    <svg
                      className="w-16 h-16 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </button>
                </div>
              )}

              {videoState.playing && videoSrc.type === "youtube" && (
                <YouTube
                  videoId={videoSrc.id}
                  opts={opts}
                  className="absolute inset-0 w-full h-full"
                  onEnd={() => setVideoState({ playing: false })}
                />
              )}

              {videoState.playing && videoSrc.type === "file" && (
                <video
                  className="absolute inset-0 w-full h-full object-cover"
                  controls
                  autoPlay
                  muted
                  onEnded={() => setVideoState({ playing: false })}
                >
                  <source src={videoSrc.url} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              )}
            </>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-800 p-4 text-center">
              <svg
                className="w-24 h-24 text-gray-400 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                ></path>
              </svg>
              <p className="text-gray-400 text-lg">
                No video available for this section
              </p>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default WhoWeAre;